import {
  showApiErrorNotification,
  type RequestQuery,
  useApiClient,
} from '@vrplatform/data';
import { FileDownloadIcon } from '@vrplatform/icons';
import { EllipsisMenuItem } from '@vrplatform/components-legacy';
import {
  showLoadingNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { useMantineTheme } from '@mantine/core';
import { type ReactNode, useId, useState } from 'react';
import { downloadBlob } from '../utils';

type SourceDownloadType = RequestQuery<'get:/sources/{id}/download'>['type'];

export const EllipsisMenuSourceDownloadItem = ({
  id,
  type,
  children,
  fileName,
  notification,
}: {
  id: string;
  type: SourceDownloadType;
  fileName: string;
  children?: ReactNode;
  notification?: string;
}) => {
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const api = useApiClient();
  const notifyId = useId();

  return (
    <EllipsisMenuItem
      customIcon={
        <FileDownloadIcon size={16} color={theme.colors.neutral[5]} />
      }
      loading={loading}
      onClick={async () => {
        setLoading(true);
        showLoadingNotification({
          id: notifyId,
        });

        try {
          const response = await api.GET('/sources/{id}/download', {
            params: {
              path: {
                id,
              },
              query: {
                type,
              },
            },
            parseAs: 'blob',
          });

          if (response.error) throw response.error;
          if (!response.data) throw new Error('No source download returned');

          const isJsonFile =
            response.data.type.toLowerCase().includes('json') ||
            fileName.toLowerCase().endsWith('.json');

          downloadBlob(response.data, fileName, {
            disposition: isJsonFile ? 'openInNewTab' : 'download',
          });

          updateSuccessNotification({
            id: notifyId,
            title: 'Success!',
            message: notification || 'Source downloaded.',
          });
        } catch (error) {
          console.error(error);
          showApiErrorNotification({
            id: notifyId,
            title: 'Failed to download source',
            error,
            defaultMessage: 'Failed to download source',
          });
        } finally {
          setLoading(false);
        }
      }}
    >
      {children || 'Download source'}
    </EllipsisMenuItem>
  );
};
